import React, { useState, useCallback, useEffect, useMemo } from "react";
import { connect } from "dva";
import { FileChatMessage, RosterApplyStatus, Session, SessionType } from "@/types/chat";
import { MessageSendType } from "@/types/chat/enum";
import ChatState from "@/types/chat/State";
import { WithTranslation } from "react-i18next";
import Upload from "./Upload";
import Download from "@/baseComponents/Chat/message/File/Download";
import IconFont from "@/components/icon";
import { Drawer, List, Tabs, Timeline } from "antd";
import {
  getFileRecordByPage,
  getFileRecordCount,
  GetFileRecordCountResp,
  getFileRecordByPerson
} from "@/api/chat";
import { ListItem } from "@jd/focus-desktop-comps";
import { ListItemData } from "@/components/ListItem";
import DefaultPage from "@/components/DefaultPage";
import dayjs from "dayjs";
import InfiniteScroll from "react-infinite-scroller";
import Loading from "@/components/Loading";
import { CompDataLoadState, DefaultPageType } from "@/models";
import { getUnitTitleInfo, isSameClient } from "@/utils/chat/message";
import { isFocusEnv } from "@/utils";
import { changeEgovUrl } from "@/utils/tools";
import bus from "@/utils/bus";
import { getUserAlias } from "@/utils/chat/group";
import { CrossTag } from "@/components/Tags";
import { canJoyspacePreview } from "@/components/Joyspace/util";

interface UploadFileProps {
  isMergeForward?: boolean;
  message: FileChatMessage;
  sendType: MessageSendType;
  sessionType: SessionType;
  selectedSession?: Session;
  onReady: (params: { beginSend: boolean; messageExtend: any }) => {};
}

interface DvaDispatchProps {
  updateSessionMessageMid: Function;
}
type IProps = Readonly<WithTranslation & UploadFileProps & DvaDispatchProps & ChatState>;

interface BaseData {
  fileId: string;
  downloadPeopleCount: number;
  downloadCount: number;
}

function UploadWithDetail(props: IProps) {
  const { message, selectedSession } = props;
  const [detailVisible, setDetailVisible] = useState(false);
  const [dataList, setDataList] = useState<ListItemData[]>([]);
  const [baseData, setBaseData] = useState<BaseData>({
    fileId: "",
    downloadPeopleCount: 0,
    downloadCount: 0
  });
  const [pageNum, setPageNum] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [tabActiveKey, setTabActiveKey] = useState("0");
  const [itemDetailVisible, setItemDetailVisible] = useState(false);
  const [itemData, setItemData] = useState<any>({});
  const [recordCountData, setRecordCountData] = useState<GetFileRecordCountResp>({
    downloadPeopleCount: 0,
    downloadCount: 0,
    previewPeopleCount: 0,
    previewCount: 0,
    saveDocPeopleCount: 0,
    saveDocCount: 0
  });
  const [timeRecordLoading, setTimeRecordLoading] = useState(false);
  const [timeRecordList, setTimeRecordList] = useState<any[]>([]);
  const [isWpsFile, setIsWpsFile] = useState(false);

  const extraText = useMemo(() => {
    const MAP: {
      [key: string]: string;
    } = {
      "1": "查看",
      "0": "下载",
      "2": "转存"
    };
    return MAP[tabActiveKey];
  }, [tabActiveKey]);

  const allPeopleCount = useMemo(() => {
    const MAP: {
      [key: string]: number;
    } = {
      "1": recordCountData.previewPeopleCount,
      "2": recordCountData.saveDocPeopleCount,
      "0": recordCountData.downloadPeopleCount
    };
    return MAP[tabActiveKey];
  }, [recordCountData, tabActiveKey]);

  const allCount = useMemo(() => {
    const MAP: {
      [key: string]: number;
    } = {
      "1": recordCountData.previewCount,
      "2": recordCountData.saveDocCount,
      "0": recordCountData.downloadCount
    };
    return MAP[tabActiveKey];
  }, [recordCountData, tabActiveKey]);

  const noticeContent = useMemo(() => {
    const MAP: { [key: string]: string } = {
      "1": "仅统计2.3.1及以上版本的文件记录",
      "0": "仅统计2.0.0及以上版本的文件记录",
      "2": "仅统计2.3.1及以上版本的文件记录"
    };
    return tabActiveKey === "0" && selectedSession.isSingle
      ? "仅统计2.3.1及以上版本的文件记录"
      : MAP[tabActiveKey];
  }, [tabActiveKey, selectedSession.isSingle]);

  const featchFileRecordCount = () => {
    getFileRecordCount({
      fileId: message.id
    }).then((res) => {
      console.log(res, "getFileRecordCount");
      if (res[0]) {
        setRecordCountData(res[0]);
      }
    });
  };

  useEffect(() => {
    if (message.name) {
      const res = canJoyspacePreview(message.name);
      setIsWpsFile(res);
      res ? setTabActiveKey("1") : setTabActiveKey("0");
    }
  }, [message]);

  const fetchDataList = useCallback(
    (pageSize = pageNum, optType) => {
      if (fetching) return;
      setFetching(true);
      getFileRecordByPage({
        fileId: message.id,
        pageNum: pageSize,
        pageSize: 30,
        optType
      }).then((res) => {
        console.log(res, "getFileRecordByPage");
        if (res[0]) {
          const data: {
            pageNum: number;
            pageSize: number;
            totalNum: number;
            totalPage: number;
            fileId: string;
            downloadPeopleCount: number;
            downloadCount: number;
            list: any[];
          } = res[0];
          const hasMore = pageSize < data.totalPage;
          setHasMore(hasMore);
          if (hasMore) {
            setPageNum(pageSize + 1);
          }
          const list = data.list.map((item: any) => ({
            id: item.userId,
            name: item.userName,
            avatar: item.avatar,
            desc: getUnitTitleInfo(item.unitName, item.post),
            ...item
          }));
          if (pageSize === 1) {
            setDataList([...list]);
          } else {
            setDataList((prev) => [...prev, ...list]);
          }
        }
        setFetching(false);
        setFetched(true);
      });
    },
    [fetching, message.id, pageNum, tabActiveKey]
  );

  const fetchFileRecordByPerson = useCallback(({ optType, userId, teamId, app }) => {
    setTimeRecordLoading(true);
    getFileRecordByPerson({
      fileId: message.id,
      optType,
      userId,
      teamId,
      app
    })
      .then((res) => {
        console.log(res, "getFileRecordByPerson");
        if (res[0]) {
          setTimeRecordList(res[0].list);
          console.log("res=>", res[0]);
        }
        setTimeRecordLoading(false);
      })
      .finally(() => {
        setTimeRecordLoading(false);
      });
  }, []);

  const visibleChange = (e: MouseEvent) => {
    if (detailVisible) {
      bus.emit("message-item-chatitem", true);
    }
  };
  useEffect(() => {
    document.addEventListener("contextmenu", visibleChange);
    return () => {
      document.removeEventListener("contextmenu", visibleChange);
    };
  });

  const showDetailModal = () => {
    setDetailVisible(true);
    const optType = Number(tabActiveKey);
    fetchDataList(1, optType);
    featchFileRecordCount();
  };

  const loadMoreData = () => {
    const optType = Number(tabActiveKey);
    fetchDataList(pageNum, optType);
  };

  const handleClose = () => {
    setDetailVisible(false);
    setHasMore(true);
    // setTabActiveKey("0");
    isWpsFile ? setTabActiveKey("1") : setTabActiveKey("0");
    setDataList([]);
    setPageNum(1);
    setFetching(false);
  };

  const handleTabChange = (key: string) => {
    setTabActiveKey(key);
    setDataList([]);
    setPageNum(1);
    // setFetching(true);
    setHasMore(true);
    const optType = Number(key);
    fetchDataList(1, optType);
    featchFileRecordCount();
  };

  const handleCloseItem = useCallback(() => {
    console.log("handleclose");
    setItemDetailVisible(false);
    setItemData({});
    setTimeRecordList([]);
    setTimeRecordLoading(false);
  }, []);

  const titleNode = useMemo(() => {
    return (
      <div>
        <IconFont
          type="iconwin_left"
          className="group-management-icon-arrow app-region-none"
          onClick={handleCloseItem}
        />
        <span className="management-title">{`${itemData.userName}共${extraText}${itemData.optCount}次`}</span>
      </div>
    );
  }, [extraText, itemData, handleCloseItem]);

  const handleClickItem = (item: ListItemData) => {
    console.log(item, "item");
    setItemDetailVisible(true);
    setItemData(item);
    const optType = Number(tabActiveKey);
    fetchFileRecordByPerson({
      optType,
      ...item
    });
  };

  return (
    <>
      <div className="upload-detail-btn" onClick={showDetailModal}>
        <span>文件记录</span>
        <IconFont
          type="iconapp_ic_details"
          style={{
            fontSize: "11px",
            color: "rgba(22,134,255,.6)",
            height: "18px",
            lineHeight: "22px"
          }}
        />
      </div>
      <Drawer
        title={
          <div className="drawer-title">
            <span className="drawer-title_name">文件操作记录</span>
          </div>
        }
        className="upload-detail-drawer"
        placement="right"
        width={340}
        onClose={handleClose}
        visible={detailVisible}
        destroyOnClose={true}
        maskClosable={true}
      >
        <div className="upload-detail-drawer-main">
          <Tabs activeKey={tabActiveKey} onChange={handleTabChange} className="upload-detail-tabs">
            {isWpsFile && (
              <Tabs.TabPane tab={`查看（${recordCountData.previewPeopleCount}人）`} key="1" />
            )}
            <Tabs.TabPane tab={`下载（${recordCountData.downloadPeopleCount}人）`} key="0" />
            <Tabs.TabPane tab={`转存（${recordCountData.saveDocPeopleCount}人）`} key="2" />
          </Tabs>
          <div className="upload-detail-static">
            <span>
              {extraText}人数：<b>{allPeopleCount}</b>人
            </span>
            <span>
              {extraText}次数：<b>{allCount}</b>次
            </span>
          </div>
          {/* <div className="upload-detail-notice">
            <IconFont type="iconapp_ic_failure" style={{ fontSize: "12px", color: "#FF7513" }} />
            <span className="notice-text">{noticeContent}</span>
          </div> */}
          <div className="upload-detail-content">
            {fetching ? (
              <div className="detail-loading-wrap">
                <Loading type="message" />
              </div>
            ) : dataList.length ? (
              <div className="upload-detail-list">
                <InfiniteScroll
                  initialLoad={false}
                  pageStart={0}
                  loadMore={loadMoreData}
                  hasMore={!fetching && hasMore}
                  useWindow={false}
                >
                  <List
                    dataSource={dataList || []}
                    renderItem={(item) => {
                      const alias = getUserAlias(
                        {
                          ...item,
                          app:
                            item.app ||
                            (window as any)?._BTS_CONFIG?.REACT_APP_DD_APPID ||
                            process.env.REACT_APP_DD_APPID ||
                            "gwork"
                        },
                        selectedSession,
                        "hideRemark"
                      );
                      const isExternal =
                        item.userType === "1" || item.userType === "4" || item.userType === "5";
                      const nameStr = alias ? `${item.name}（${alias}）` : `${item.name}`;
                      let newItem = {
                        ...item,
                        name: nameStr,
                        ...{
                          avatar: changeEgovUrl(item.avatar),
                          externalTag: isExternal && <CrossTag />
                        }
                      };
                      return (
                        <ListItem
                          data={newItem}
                          extra={
                            <>
                              <div className="download-time">
                                {dayjs(item.lastOptTime).format("YYYY.M.D HH:mm:ss")}
                              </div>
                              <div className="times" onClick={() => handleClickItem(newItem)}>
                                {`共${extraText}${(newItem as any).optCount}次`}
                                <IconFont
                                  type="iconapp_ic_details"
                                  style={{
                                    fontSize: "11px",
                                    color: "#1686FF",
                                    height: "18px",
                                    lineHeight: "22px"
                                  }}
                                />
                              </div>
                            </>
                          }
                          className="upload-detail-item"
                          tag={isExternal && <CrossTag />}
                        />
                      );
                    }}
                  />
                  {/* {!hasMore && <Divider plain>暂无更多数据~</Divider>} */}
                </InfiniteScroll>
              </div>
            ) : (
              <DefaultPage
                dataType={CompDataLoadState.NODATA}
                pageType={DefaultPageType.KEYWORD}
                description="暂无数据"
              />
            )}
          </div>
        </div>
      </Drawer>
      <Drawer
        title={titleNode}
        className="upload-detail-drawer"
        placement="right"
        width={340}
        onClose={handleCloseItem}
        visible={itemDetailVisible}
        destroyOnClose={true}
        maskClosable={true}
        zIndex={1001}
        closable={false}
      >
        <div className="item-detail-drawer-main">
          <ListItem
            data={itemData as ListItemData}
            className="item-detail-item"
            tag={(itemData as ListItemData).externalTag}
          />
          <div className="item-detail-label">{extraText}时间</div>
          <div className="time-line-container">
            {timeRecordLoading ? (
              <div className="detail-loading-wrap">
                <Loading type="message" />
              </div>
            ) : (
              <Timeline>
                {timeRecordList.map((item) => (
                  <Timeline.Item key={item.downloadTime}>
                    <div className="time-line-item">
                      {dayjs(item.downloadTime).format("YYYY.M.D HH:mm:ss")}
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
}

function mapStateToProps({ chat }: any) {
  const { selectedSession } = chat as ChatState;
  return { selectedSession };
}

export default connect(mapStateToProps)(UploadWithDetail);
